<template>
  <div class="desk-event-set-editor">
    <div class="desk-event-set-editor__days my-2">
      <button
        @click="() => toggleDay(dayIndex)"
        v-for="[dayIndex, label] in days"
        :key="dayIndex"
        :class="['btn btn-default btn-xs', { active: isSelected(dayIndex) }]"
      >
        {{ label }}
      </button>
    </div>
    <div>
      <label class="form-check-label">Ends:</label>
      <div style="margin-left: 20px">
        <div class="app-form-row my-1" v-if="!limited">
          <label class="form-check-label">
            <input class="form-check-input" type="radio" :checked="until === ''" @change="until = ''" />
            Never
          </label>
        </div>
        <div class="app-form-row my-1 desk-event-set-editor__date-row">
          <label class="form-check-label">
            <input class="form-check-input" type="radio" :checked="until !== '' || limited" @change="until = new Date().toISOString()" />
            On
          </label>
          <app-date-select
            small
            class="app-form-control mr-1"
            :date="untilDate"
            :disabled="!until && !limited"
            :max-date="maxDate"
            @change="until = $event.toDate().toISOString()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { RRule } from 'rrule'
import { isEqual } from 'lodash'
import AppDateSelect from '@/components/shared/AppDateSelect'
import { Api } from '@/utils/Api'
import UrlStore from '@/utils/UrlStore'
import { EventSaveTypes, MAX_DAYS_DESK_BOOKING_LIMIT } from '@/components/desk/helpers'
import Id, { isLocalId } from '@/utils/Id'
import dayjs from 'dayjs'
import { SERVER_DATE_FORMAT } from '@/components/desk/utils'

const deskEventSetsApi = new Api(UrlStore.DESK_EVENT_SETS)

export default {
  name: 'DeskEventSetEditor',
  components: {
    AppDateSelect
  },
  props: {
    clientEvent: { type: Object, required: true },
    eventSet: { type: Object, default: () => {} },
    limited: { type: Boolean, default: false }
  },
  data() {
    return {
      EventSaveTypes,
      initialEventStart: '',
      dtstart: '',
      until: '',
      selectedDays: [],
      days: [
        [6, 'SU'],
        [0, 'MO'],
        [1, 'TU'],
        [2, 'WE'],
        [3, 'TH'],
        [4, 'FR'],
        [5, 'SA']
      ]
    }
  },
  computed: {
    maxDate() {
      if (this.limited) {
        return dayjs().add(MAX_DAYS_DESK_BOOKING_LIMIT, 'day')
      }
      return undefined
    },
    untilDate() {
      return this.until ? dayjs(this.until) : null
    },
    hasChanges() {
      return !isEqual(this.eventSet, this.computedEventSet)
    },
    computedEventSet() {
      return {
        id: Id(),
        desk_id: this.clientEvent.desk_id,
        employee_id: this.clientEvent.employee_id,
        rrule: this.rrule,
        duration_minutes: this.clientEvent.end && this.clientEvent.end.diff(this.clientEvent.start, 'minutes'),
        remind_minutes: this.clientEvent.remind
      }
    },
    rrule() {
      const options = {
        freq: 3, // Frequency.DAILY
        dtstart: this.dtstart,
        byweekday: this.selectedDays
      }
      if (this.until) options.until = this.until
      return new RRule(options).toString()
    }
  },
  methods: {
    isSelected(day) {
      return this.selectedDays.includes(day)
    },
    toggleDay(dayIndex) {
      const index = this.selectedDays.findIndex((v) => v === dayIndex)
      index === -1 ? this.selectedDays.push(dayIndex) : this.selectedDays.splice(index, 1)
    },
    initRrule() {
      if (this.eventSet.rrule) {
        const rrule = RRule.fromString(this.eventSet.rrule)
        this.selectedDays = [...rrule.options.byweekday]
        this.until = rrule.options.until
      }
    },
    save(updateType) {
      const eventSet = { ...this.computedEventSet, update_type: updateType }
      if (!isLocalId(this.clientEvent.id)) eventSet.event_source_id = this.clientEvent.id
      return deskEventSetsApi.save(eventSet)
    },
    async destroy(updateType) {
      const now = dayjs()
        .add(5, 'seconds')
        .toDate()
      if (updateType === EventSaveTypes.All) {
        let rrule = RRule.fromString(this.eventSet.rrule)
        this.dtstart = new Date(Math.max(rrule.options.dtstart, now))
      } else {
        this.dtstart = new Date(Math.max(this.clientEvent.start.subtract(1, 'seconds').toDate(), now))
      }
      this.until = dayjs(this.dtstart)
        .subtract(1, 'seconds')
        .toDate()
      try {
        await this.save(updateType)
        this.$appToast.success('Booking deleted')
        return true
      } catch (error) {
        if (error.response) {
          if (error.response.status === 404) {
            this.$appToast.error('Booking not found!')
          } else if (error.response.status === 500) {
            this.$appToast.error('Internal server on booking delete!')
          }
        } else {
          throw error
        }
      }
    },
    getDtstartByClientEvent(eventStart) {
      if (!this.eventSet.rrule) return eventStart.toDate()

      const rrule = RRule.fromString(this.eventSet.rrule)
      if (eventStart.isSame(this.initialEventStart)) {
        // don't have any event start changes keep origin date
        return rrule.options.dtstart
      } else {
        const rruleStart = dayjs(rrule.options.dtstart)
        if (eventStart.format(SERVER_DATE_FORMAT) !== this.initialEventStart.format(SERVER_DATE_FORMAT)) {
          // start date changed
          return eventStart.toDate()
        } else {
          // set time from eventStart anyway
          return rruleStart
            .hour(eventStart.hour())
            .minute(eventStart.minute())
            .toDate()
        }
      }
    }
  },
  mounted() {
    this.initRrule()
    this.initialEventStart = dayjs(this.clientEvent.start)
    this.dtstart = this.getDtstartByClientEvent(this.clientEvent.start)
    if (this.limited) {
      this.until = dayjs(this.clientEvent.start).add(5, 'day')
    }
  },
  watch: {
    'clientEvent.start': {
      handler(v) {
        this.dtstart = this.getDtstartByClientEvent(v)
      }
    }
  }
}
</script>

<style lang="scss">
.desk-event-set-editor {
  margin-block: 5px;
  &__days {
    display: flex;
    gap: 5px;
  }
  &__date-row {
    display: flex;
    gap: 5px;
  }
}
</style>
